import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { clientsCompanyResource, usersResource } from "../../helpers/configRoutes/backend_helper";

const Profile = () => {
    const navigate = useNavigate();
    const authUser = useSelector((state) => state.Login.user);
    const safeAuthUser = useMemo(() => authUser || {}, [authUser]);

    const [user, setUser] = useState(safeAuthUser);
    const [company, setCompany] = useState(safeAuthUser.company || null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setUser(safeAuthUser || {});
    }, [safeAuthUser]);

    useEffect(() => {
        const fetchProfile = async () => {
            if (!safeAuthUser?.id) return;
            setLoading(true);

            const [userResponse, companyResponse] = await Promise.all([
                usersResource("show", {}, safeAuthUser.id),
                safeAuthUser.company_id
                    ? clientsCompanyResource("show", {}, safeAuthUser.company_id)
                    : Promise.resolve(null),
            ]);

            if (userResponse?.status && userResponse?.user) {
                setUser((currentUser) => ({ ...currentUser, ...userResponse.user }));
            }

            if (companyResponse?.status && companyResponse?.company) {
                setCompany(companyResponse.company);
            }

            setLoading(false);
        };

        fetchProfile();
    }, [safeAuthUser?.id, safeAuthUser?.company_id]);

    const fullName = useMemo(() => {
        const fromNames = `${user?.first_name || ""} ${user?.last_name || ""}`.trim();
        return user?.name || fromNames || "User profile";
    }, [user?.first_name, user?.last_name, user?.name]);

    const initials = useMemo(() => {
        return fullName
            .split(" ")
            .filter(Boolean)
            .map((part) => part[0])
            .join("")
            .slice(0, 2)
            .toUpperCase();
    }, [fullName]);

    const phone = user?.phone
        ? `${user?.prefix?.prefix ? `${user.prefix.prefix} ` : ""}${user.phone}`
        : "Not registered";

    const profileLabel = user?.profile_id === 3 ? "Operator" : "Dispatch user";
    const statusLabel = user?.active ? "Active" : "Suspended";

    return (
        <div className="min-h-screen bg-cs-surface">
            <div className="max-w-6xl mx-auto space-y-5">
                <section className="rounded-3xl border border-cs-border bg-gradient-to-r from-cs-primary-dark to-cs-primary p-6 text-white shadow-sm overflow-hidden relative">
                    <div className="absolute right-0 top-0 h-full w-1/3 bg-white/5 rounded-l-full blur-2xl" />
                    <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
                        <div className="flex items-center gap-4">
                            <div className="w-20 h-20 rounded-3xl bg-white/15 border border-white/20 flex items-center justify-center overflow-hidden shadow-sm">
                                {user?.imageProfile ? (
                                    <img
                                        src={user.imageProfile}
                                        alt={fullName}
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <span className="text-2xl font-semibold">{initials}</span>
                                )}
                            </div>
                            <div>
                                <p className="text-xs uppercase tracking-[0.18em] text-white/70">
                                    My profile
                                </p>
                                <h1 className="text-2xl font-semibold mt-1">{fullName}</h1>
                                <p className="text-sm text-white/75 mt-1">
                                    {user?.email || "No email registered"}
                                </p>
                                <div className="flex flex-wrap gap-2 mt-3">
                                    <span className="px-2.5 py-1 rounded-full bg-white/15 text-xs border border-white/15">
                                        {profileLabel}
                                    </span>
                                    <span className="px-2.5 py-1 rounded-full bg-white/15 text-xs border border-white/15">
                                        {statusLabel}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-wrap gap-2">
                            <button
                                type="button"
                                onClick={() => navigate(-1)}
                                className="px-3 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-xs font-medium transition-all border border-white/10"
                            >
                                Back
                            </button>
                            <Link
                                to="/acount-profile"
                                className="px-3 py-2 rounded-xl bg-white text-cs-primary-dark hover:bg-white/90 text-xs font-semibold transition-all shadow-sm"
                            >
                                Account & billing
                            </Link>
                        </div>
                    </div>
                </section>

                {loading && (
                    <div className="rounded-2xl border border-cs-border bg-cs-bg px-4 py-3 text-xs text-cs-muted">
                        Refreshing profile information...
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <section className="lg:col-span-2 rounded-3xl border border-cs-border bg-cs-bg p-5 shadow-sm">
                        <div className="flex items-start justify-between gap-3 mb-4">
                            <div>
                                <h2 className="text-base font-semibold text-cs-dark">Personal information</h2>
                                <p className="text-xs text-cs-muted mt-1">
                                    Basic profile details used across dispatch workflows and notifications.
                                </p>
                            </div>
                            <span className="material-symbols-outlined text-cs-primary bg-cs-primary-soft rounded-2xl p-2">
                                badge
                            </span>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                            <InfoItem label="First name" value={user?.first_name || "Not registered"} />
                            <InfoItem label="Last name" value={user?.last_name || "Not registered"} />
                            <InfoItem label="Email" value={user?.email || "Not registered"} />
                            <InfoItem label="Phone" value={phone} />
                            <InfoItem label="Role" value={user?.role || profileLabel} />
                            <InfoItem label="Status" value={statusLabel} />
                        </div>
                    </section>

                    <section className="rounded-3xl border border-cs-border bg-cs-bg p-5 shadow-sm">
                        <div className="flex items-start justify-between gap-3 mb-4">
                            <div>
                                <h2 className="text-base font-semibold text-cs-dark">Company</h2>
                                <p className="text-xs text-cs-muted mt-1">
                                    Transport office linked to this account.
                                </p>
                            </div>
                            <span className="material-symbols-outlined text-cs-primary bg-cs-primary-soft rounded-2xl p-2">
                                domain
                            </span>
                        </div>

                        <div className="space-y-3 text-sm">
                            <InfoItem label="Company" value={company?.name || "Not registered"} stacked />
                            <InfoItem label="Contact" value={company?.contact_name || "Not registered"} stacked />
                            <InfoItem label="Phone" value={company?.phone || "Not registered"} stacked />
                            <InfoItem label="DOT" value={company?.dot || "Not registered"} stacked />
                        </div>
                    </section>
                </div>

                <section className="rounded-3xl border border-cs-border bg-cs-bg p-5 shadow-sm">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                        <div>
                            <h2 className="text-base font-semibold text-cs-dark">Quick actions</h2>
                            <p className="text-xs text-cs-muted mt-1">
                                Shortcuts related to your account and workspace.
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <Link
                                to="/configuration-alerts"
                                className="px-3 py-2 rounded-xl border border-cs-border bg-cs-bg-alt hover:bg-cs-primary-soft hover:text-cs-primary text-xs text-cs-muted transition-all"
                            >
                                Notification settings
                            </Link>
                            <Link
                                to="/team"
                                className="px-3 py-2 rounded-xl border border-cs-border bg-cs-bg-alt hover:bg-cs-primary-soft hover:text-cs-primary text-xs text-cs-muted transition-all"
                            >
                                Team users
                            </Link>
                            <Link
                                to="/support"
                                className="px-3 py-2 rounded-xl bg-cs-primary text-white hover:bg-cs-primary-hover text-xs font-medium transition-all"
                            >
                                Support
                            </Link>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    );
};

const InfoItem = ({ label, value, stacked = false }) => (
    <div className={`rounded-2xl border border-cs-border bg-cs-surface px-3 py-3 ${stacked ? "" : "min-h-[72px]"}`}>
        <p className="text-[10px] uppercase tracking-[0.14em] text-cs-muted">{label}</p>
        <p className="text-sm font-medium text-cs-dark mt-1 break-words">{value}</p>
    </div>
);

export default Profile;
